import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useOutletContext, useParams, useNavigate } from 'react-router-dom'
import { useI18n } from 'gunsi-auth'
import { useTally, useMonthTransactions, useRangeTransactions } from '../hooks/useTally.jsx'
import { useTallyI18n } from '../i18n/tally.js'
import { useIsDesktop } from '../useIsDesktop.js'
import { formatAmount, formatTime, formatDay, weekdayShort, dayKey, displayDate, expandAllSlices, addInterval, wallClock, wallClockDate, wallClockKey } from '../format.js'
import { Emoji } from '../components/Emoji.jsx'
import { CategoryPicker } from '../components/CategoryPicker.jsx'
import { CategoryIcon } from '../components/CategoryIcon.jsx'
import { CurrencySelect } from '../components/CurrencySelect.jsx'
import { MonthPicker } from '../components/MonthPicker.jsx'
import { AmountInput } from '../components/AmountInput.jsx'
import { IntegerInput } from '../components/IntegerInput.jsx'
import { SimpleSelect } from '../components/SimpleSelect.jsx'
import { NameInput } from '../components/NameInput.jsx'
import { lastUsedCurrency } from '../recentCurrencies.js'
import { isSystemCategory } from '../categoryTree.js'
import { DatePicker } from '../components/DatePicker.jsx'
import { TimePicker } from '../components/TimePicker.jsx'
import {
  shiftLilMonth, lilMonthRange,
  formatLilMonthJsx, formatLilYear, lilDayKey, lilDayInfo,
  lilToHe, heToLil, todayLil,
} from '../lil.jsx'

function shiftYearMonth(ym, delta) {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// gunsi-ledger mark — the brand L glyph (no background variant).
function LedgerMark() {
  return (
    <img
      src="/icons/ledger-mark.svg"
      alt=""
      width="22"
      height="22"
      style={{ display: 'block' }}
    />
  )
}

// gunsi-even mark — l'arte ufficiale (i due gemelli che si stringono la mano)
// nel colore del brand e senza il fondo dell'app, come il marchio di Ledger.
function EvenMark() {
  return (
    <img
      src="/icons/even-mark.svg"
      alt=""
      width="22"
      height="22"
      style={{ display: 'block' }}
    />
  )
}

function formatYearMonth(ym, locale) {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 1, 1)
  const lang = (locale || document.documentElement.lang || 'en').startsWith('it') ? 'it' : 'en'
  return new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }).format(d)
}

export function Transactions() {
  const t = useTallyI18n()
  const { locale } = useI18n()
  const { subTopbar } = useOutletContext()
  const {
    data, addTransaction, updateTransaction, removeTransaction,
    viewedGreg: viewedMonth, setViewedGreg: setViewedMonth,
    viewedLil, setViewedLil,
  } = useTally()
  const navigate = useNavigate()
  const params   = useParams()
  // Read prefs from the synced user_data settings (was localStorage before).
  const calendar = data.calendar    ?? 'gregorian'
  const yearEra  = data.yearEra     ?? 'lil'
  const dayCal   = data.dayCalendar ?? 'lil'
  const isLil = calendar === 'lil'
  // Day labels follow `dayCal` only when month nav is Lil; otherwise always Greg
  const useLilDays = isLil && dayCal === 'lil'

  // null = closed, {} = create, {id, ...} = edit
  const [editing, setEdit] = useState(null)
  const [pickerOpen, setPickerOpen]   = useState(false)

  // Totals row mode: 'total' = month sums, 'daily' = per-day averages.
  // UI-only pref, persisted to localStorage (survives reloads, per-device).
  const [totalsMode, setTotalsMode] = useState(() => {
    try { return localStorage.getItem('gunsi_tally_totals_mode') === 'daily' ? 'daily' : 'total' }
    catch { return 'total' }
  })
  useEffect(() => {
    try { localStorage.setItem('gunsi_tally_totals_mode', totalsMode) } catch { /* ignore */ }
  }, [totalsMode])
  const dailyMode = totalsMode === 'daily'

  // ── URL ↔ state sync ─────────────────────────────────────────────────
  // URL pattern (mirrors Stats):
  //   /transactions                 → current month
  //   /transactions/<year>/<month>  → specific month; <month> is 1..12 (greg)
  //                                    or 1..13 / 'S' (lil)
  // <year> is in the active era — for Lil + yearEra='he' it's the HE year.
  useEffect(() => {
    if (!params.year || !params.month) return
    const y = Number(params.year)
    if (!Number.isFinite(y)) return
    if (isLil) {
      const lilYear = yearEra === 'he' ? heToLil(y) : y
      if (params.month === 'S' || params.month === 's') {
        setViewedLil({ year: lilYear, special: true })
      } else {
        const m = Number(params.month)
        if (Number.isFinite(m) && m >= 1 && m <= 13) {
          setViewedLil({ year: lilYear, month: m })
        }
      }
    } else {
      const m = Number(params.month)
      if (Number.isFinite(m) && m >= 1 && m <= 12) {
        setViewedMonth(`${y}-${String(m).padStart(2, '0')}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.year, params.month, isLil, yearEra])

  // Reflect state into URL (replace so back-button doesn't walk through every
  // month visited via swipe / arrows).
  useEffect(() => {
    let path
    if (isLil) {
      const y = yearEra === 'he' ? lilToHe(viewedLil.year) : viewedLil.year
      const m = viewedLil.special ? 'S' : viewedLil.month
      path = `/transactions/${y}/${m}`
    } else {
      const [y, m] = viewedMonth.split('-').map(Number)
      path = `/transactions/${y}/${m}`
    }
    if (window.location.pathname !== path) {
      navigate(path, { replace: true })
    }
  }, [isLil, yearEra, viewedMonth, viewedLil, navigate])

  // Compute Gregorian range for the viewed Lil month (only used in Lil mode)
  const lilRange = useMemo(
    () => (isLil ? lilMonthRange(viewedLil) : { from: null, to: null }),
    [isLil, viewedLil],
  )

  // Hooks: call BOTH unconditionally (React rule), only one has a non-null key.
  const lilCacheKey = isLil
    ? `lil:${viewedLil.year}-${viewedLil.special ? 'S' : viewedLil.month}`
    : null
  const gregView = useMonthTransactions(isLil ? null : viewedMonth)
  const lilView  = useRangeTransactions(lilCacheKey, lilRange.from, lilRange.to)
  const { transactions, loading } = isLil ? lilView : gregView

  const monthLabel = useMemo(
    () => (isLil ? formatLilMonthJsx(viewedLil, yearEra) : formatYearMonth(viewedMonth, locale)),
    [isLil, yearEra, viewedMonth, viewedLil, locale],
  )

  function jumpToMonth(target) {
    // `target` is a "YYYY-MM" string (greg) or {year, month} (lil). No swipe
    // animation on direct jumps — the swipe direction would be meaningless.
    if (isLil) setViewedLil(target)
    else       setViewedMonth(target)
  }

  // Tracks the last navKey we auto-scrolled for. Reset when leaving the
  // current month, so re-entering it scrolls again. Updates without
  // re-scrolling when the user adds/removes tx in the current month.
  const lastScrolledKeyRef = useRef(null)

  // Refs for swipe animation (touch + arrow click)
  const stripRef        = useRef(null)
  const startXRef       = useRef(null)
  const startYRef       = useRef(null)
  const directionRef    = useRef(null)
  const currentDxRef    = useRef(0)
  const busyRef         = useRef(false)
  const lastNavDirRef   = useRef(null) // 'next' | 'prev' — set right before shifting
  const isLilRef        = useRef(isLil)
  useEffect(() => { isLilRef.current = isLil }, [isLil])

  const ANIM_MS = 150

  function shiftViewed(delta) {
    lastNavDirRef.current = delta > 0 ? 'next' : 'prev'
    if (isLil) setViewedLil(v => shiftLilMonth(v, delta))
    else       setViewedMonth(v => shiftYearMonth(v, delta))
  }

  // navKey changes whenever the visible month changes (Lil or Greg)
  const navKey = useMemo(
    () => (isLil ? (lilCacheKey ?? 'lil:?') : `greg:${viewedMonth}`),
    [isLil, lilCacheKey, viewedMonth],
  )

  // Is the user currently viewing the month that contains today?
  const isCurrentMonth = useMemo(() => {
    if (isLil) {
      const tl = todayLil()
      if (tl.special) return !!viewedLil.special && viewedLil.year === tl.year
      return !viewedLil.special
        && viewedLil.year === tl.year
        && viewedLil.month === tl.month
    }
    const now = new Date()
    const todayYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return viewedMonth === todayYM
  }, [isLil, viewedMonth, viewedLil])

  // After the new month is rendered, slide the strip in from the opposite side
  useLayoutEffect(() => {
    const strip = stripRef.current
    if (!strip) return
    const dir = lastNavDirRef.current
    if (!dir) return // initial mount or calendar-mode flip — no animation
    lastNavDirRef.current = null
    const w = strip.offsetWidth || window.innerWidth
    strip.style.transition = 'none'
    strip.style.transform  = `translateX(${dir === 'next' ? w : -w}px)`
    void strip.offsetWidth // force reflow so the next transition starts fresh
    strip.style.transition = `transform ${ANIM_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
    strip.style.transform  = 'translateX(0)'
  }, [navKey])

  // Touch swipe handlers (mobile-only; desktop users have the arrows)
  useEffect(() => {
    const strip = stripRef.current
    if (!strip) return

    function onTouchStart(e) {
      if (busyRef.current) return
      startXRef.current = e.touches[0].clientX
      startYRef.current = e.touches[0].clientY
      directionRef.current = null
      currentDxRef.current = 0
      strip.style.transition = 'none'
    }

    function onTouchMove(e) {
      if (startXRef.current == null || busyRef.current) return
      const touch = e.touches[0]
      const dx = touch.clientX - startXRef.current
      const dy = touch.clientY - startYRef.current
      if (directionRef.current === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          directionRef.current = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v'
        }
        return
      }
      if (directionRef.current === 'h') {
        // Once vertical scroll has begun, the browser marks subsequent
        // touchmoves as non-cancelable. Skip preventDefault there to avoid
        // the [Intervention] warning.
        if (e.cancelable) e.preventDefault()
        currentDxRef.current = dx
        strip.style.transform = `translateX(${dx}px)`
      }
    }

    function onTouchEnd() {
      if (startXRef.current == null || busyRef.current) return
      const dx = currentDxRef.current
      startXRef.current = null
      currentDxRef.current = 0

      if (directionRef.current !== 'h' || Math.abs(dx) < 50) {
        strip.style.transition = `transform ${ANIM_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
        strip.style.transform = 'translateX(0)'
        return
      }

      busyRef.current = true
      const goNext = dx < 0
      const w = strip.offsetWidth || window.innerWidth
      strip.style.transition = `transform ${ANIM_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
      strip.style.transform  = `translateX(${goNext ? -w : w}px)`

      setTimeout(() => {
        lastNavDirRef.current = goNext ? 'next' : 'prev'
        if (isLilRef.current) setViewedLil(v => shiftLilMonth(v, goNext ? +1 : -1))
        else                  setViewedMonth(v => shiftYearMonth(v, goNext ? +1 : -1))
        busyRef.current = false
      }, ANIM_MS)
    }

    strip.addEventListener('touchstart', onTouchStart, { passive: true })
    strip.addEventListener('touchmove',  onTouchMove,  { passive: false })
    strip.addEventListener('touchend',   onTouchEnd)
    return () => {
      strip.removeEventListener('touchstart', onTouchStart)
      strip.removeEventListener('touchmove',  onTouchMove)
      strip.removeEventListener('touchend',   onTouchEnd)
    }
  }, [])

  // Expand split tx (one parent doc → N virtual slices). Done once here so
  // every downstream consumer (totals, day groups) iterates the same list.
  // Slices that fall OUTSIDE the currently-viewed range are filtered out so
  // a 4-week split with parent visible in May→Jun doesn't bleed extras into
  // the May view. The visible range comes from gregView/lilView so the
  // filter mirrors the backend's overlap query exactly.
  const visibleRange = useMemo(() => {
    if (isLil) return lilRange
    const [y, m] = viewedMonth.split('-').map(Number)
    const pad = (n) => String(n).padStart(2, '0')
    const ny = m === 12 ? y + 1 : y
    const nm = m === 12 ? 1 : m + 1
    return { from: `${y}-${pad(m)}-01`, to: `${ny}-${pad(nm)}-01` }
  }, [isLil, lilRange, viewedMonth])

  const expandedTx = useMemo(() => {
    const expanded = expandAllSlices(transactions)
    const { from, to } = visibleRange
    if (!from || !to) return expanded
    return expanded.filter(tx => {
      const d = (displayDate(tx) || '').slice(0, 10)
      return d >= from && d < to
    })
  }, [transactions, visibleRange])

  // Month totals (in user's default currency, using amount_base with fallback).
  // Operate on expanded slices so a 600€ split-into-4 visible across two
  // months contributes only what's in the current month.
  const monthTotals = useMemo(() => {
    let income = 0, expense = 0
    for (const tx of expandedTx) {
      const v = tx.amount_base ?? tx.amount ?? 0
      if (tx.type === 'income')  income  += v
      if (tx.type === 'expense') expense += v
    }
    return { income, expense, net: income - expense }
  }, [expandedTx])

  // Expenses already incurred (display date ≤ today). This is the numerator
  // that belongs with the elapsed-days divisor below: the month's FULL expense
  // total includes future-dated spending (flights booked ahead, the remaining
  // instalments of a split), and dividing that by the few days elapsed makes
  // the daily rate explode — on the 2nd of a month it reads as hundreds of
  // euros a day. Outside the current month there is no future part to drop.
  const expenseSoFar = useMemo(() => {
    if (!isCurrentMonth) return monthTotals.expense
    const n = new Date()
    const p = (x) => String(x).padStart(2, '0')
    const todayKey = `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`
    let sum = 0
    for (const tx of expandedTx) {
      if (tx.type !== 'expense') continue
      const k = dayKey(displayDate(tx))
      if (k && k <= todayKey) sum += tx.amount_base ?? tx.amount ?? 0
    }
    return sum
  }, [expandedTx, isCurrentMonth, monthTotals.expense])

  // Divisors for the daily-average view. Two different ones on purpose:
  //  • income → FULL length of the month: salary/income lands in bulk, so
  //    spreading it over every day gives the equivalent daily income.
  //  • expense → days ELAPSED so far (current month only): spending happens
  //    day by day, so this reflects the actual daily spending pace to date.
  // For past/future months both collapse to the full month length.
  const { dayCount, elapsedDays } = useMemo(() => {
    const { from, to } = visibleRange
    if (!from || !to) return { dayCount: 1, elapsedDays: 1 }
    const MS = 86400000
    const start = new Date(from + 'T00:00:00')
    const end = new Date(to + 'T00:00:00') // exclusive upper bound
    const full = Math.max(1, Math.round((end - start) / MS))
    let elapsedEnd = end
    if (isCurrentMonth) {
      const now = new Date()
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
      if (tomorrow < elapsedEnd) elapsedEnd = tomorrow
    }
    const elapsed = Math.max(1, Math.round((elapsedEnd - start) / MS))
    return { dayCount: full, elapsedDays: elapsed }
  }, [visibleRange, isCurrentMonth])

  // Values shown in the totals row: raw sums (totals mode, always full month)
  // or per-day averages. Net stays consistent as shown-income − shown-expense.
  const shownTotals = dailyMode
    ? {
        income:  monthTotals.income / dayCount,
        expense: expenseSoFar / elapsedDays,
        net:     monthTotals.income / dayCount - expenseSoFar / elapsedDays,
      }
    : monthTotals

  // Group by day. Bucketing uses Greg dayKey (1:1 with Lil days, simpler keys);
  // the LABEL is what changes between Lil/Greg display modes.
  // `displayDate(tx)` falls back to tx.date when no effectiveDate is set — so
  // a "future-effective" tx appears under its effective day, not its payment day.
  // Splits break the "iteration order == date desc" assumption (slice 1 of
  // a parent appears in expandedTx between siblings of slice 0), so we sort
  // both the groups AND the items within each group explicitly.
  const dayGroups = useMemo(() => {
    const map = new Map()
    for (const tx of expandedTx) {
      const dispIso = displayDate(tx)
      const k = dayKey(dispIso)
      // Wall-clock day at local noon — timezone-independent, safe to re-serialise
      if (!map.has(k)) map.set(k, { date: wallClockDate(dispIso), items: [] })
      map.get(k).items.push(tx)
    }
    for (const g of map.values()) {
      g.items.sort((a, b) =>
        wallClockKey(displayDate(b)).localeCompare(wallClockKey(displayDate(a)))
      )
    }
    return [...map.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
  }, [expandedTx])

  // When entering the current month, auto-scroll so today's group is at
  // the top of the viewport. If today has no tx, fall back to the closest
  // earlier day with tx (so the user lands on "today or just-before").
  // Only runs once per navKey — adding/removing tx in the current month
  // doesn't re-scroll.
  useEffect(() => {
    if (!isCurrentMonth) {
      lastScrolledKeyRef.current = null
      return
    }
    if (loading) return
    if (dayGroups.length === 0) return
    if (lastScrolledKeyRef.current === navKey) return

    const todayK = nowDate() // device-local today (YYYY-MM-DD == dayKey format)
    // Day groups are sorted desc → find first key ≤ today.
    const target = dayGroups.find(([k]) => k <= todayK) ?? dayGroups[0]
    if (!target) return
    lastScrolledKeyRef.current = navKey
    // Defer to next frame so the DOM is settled (especially after a swipe
    // animation just remounted the strip).
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-day-key="${target[0]}"]`)
      if (el) el.scrollIntoView({ block: 'start', behavior: 'auto' })
    })
  }, [isCurrentMonth, loading, dayGroups, navKey])

  // Resolve a clicked row (which may be a virtual slice with id "parent#i")
  // back to the original parent tx so the modal edits the source, not a slice.
  function openEdit(rowTx) {
    if (rowTx._parentId) {
      const parent = transactions.find(t => t.id === rowTx._parentId)
      setEdit(parent ?? rowTx)
    } else {
      setEdit(rowTx)
    }
  }

  const stickyContent = (
    <div className="tx-sticky-head">
      <div className="tx-totals">
        <button
          type="button"
          className={`tx-totals-toggle${dailyMode ? ' is-active' : ''}`}
          onClick={() => setTotalsMode(m => (m === 'daily' ? 'total' : 'daily'))}
          title={dailyMode ? t.transactions.monthTotalsHint : t.transactions.dailyAvgHint}
          aria-label={dailyMode ? t.transactions.monthTotalsHint : t.transactions.dailyAvgHint}
          aria-pressed={dailyMode}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="8" y1="2" x2="8" y2="5" />
            <line x1="16" y1="2" x2="16" y2="5" />
            <circle cx="12" cy="15" r="1.6" fill="currentColor" stroke="none" />
          </svg>
        </button>
        <div className="tx-total-cell">
          <span className="tx-total-label">
            {t.dashboard.income}{dailyMode && <span className="tx-total-suffix">{t.transactions.perDay}</span>}
          </span>
          <span className="tx-total-val amount-income">
            {formatAmount(shownTotals.income, data.currency)}
          </span>
        </div>
        <div className="tx-total-cell">
          <span className="tx-total-label">
            {t.dashboard.expenses}{dailyMode && <span className="tx-total-suffix">{t.transactions.perDay}</span>}
          </span>
          <span className="tx-total-val amount-expense">
            {formatAmount(shownTotals.expense, data.currency)}
          </span>
        </div>
        <div className="tx-total-cell">
          <span className="tx-total-label">
            {t.dashboard.net}{dailyMode && <span className="tx-total-suffix">{t.transactions.perDay}</span>}
          </span>
          <span className={`tx-total-val ${shownTotals.net >= 0 ? 'amount-income' : 'amount-expense'}`}>
            {shownTotals.net < 0 ? '−' : ''}{formatAmount(Math.abs(shownTotals.net), data.currency)}
          </span>
        </div>
      </div>

      <header className="view-header view-header-month">
        <div className="month-nav">
          <button
            className="month-nav-arrow"
            onClick={() => shiftViewed(-1)}
            aria-label="Previous month"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className={`month-nav-label${isLil && viewedLil.special ? ' is-range' : ''}`}
            onClick={() => setPickerOpen(true)}
          >
            {monthLabel}
          </button>
          <button
            className="month-nav-arrow"
            onClick={() => shiftViewed(+1)}
            aria-label="Next month"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
        <button
          className="fab-add"
          onClick={() => setEdit({})}
          aria-label={t.transactions.add}
          title={t.transactions.add}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </header>
    </div>
  )

  return (
    <>
      {subTopbar && createPortal(stickyContent, subTopbar)}
      <MonthPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        mode={isLil ? 'lil' : 'greg'}
        viewed={isLil ? viewedLil : viewedMonth}
        onSelect={jumpToMonth}
        yearEra={yearEra}
        locale={locale}
        t={t}
      />
      <div className="view view-tx">
      <div className="tx-swipe-strip" ref={stripRef}>
      {loading ? (
        <div className="view-empty"><div className="loading-spinner" /></div>
      ) : dayGroups.length === 0 ? (
        <div className="view-empty">
          <span>{t.transactions.empty}</span>
        </div>
      ) : (
        <div className="tx-groups">
          {dayGroups.map(([key, { date, items }]) => {
            // Day income / expense shown separately (not netted). Uses the
            // server-computed `amount_base` (each tx converted to the user's
            // default currency via EUR cross-rate); tx without `amount_base`
            // (rate unavailable) falls back to the native amount.
            const { dayIncome, dayExpense } = items.reduce((a, tx) => {
              const v = tx.amount_base ?? tx.amount ?? 0
              if (tx.type === 'income') a.dayIncome += v
              else                      a.dayExpense += v
              return a
            }, { dayIncome: 0, dayExpense: 0 })
            // Day totals are always displayed in the user's default currency
            const dayCur = data.currency

            const lilInfo = useLilDays ? lilDayInfo(date.toISOString()) : null
            return (
              <div key={key} className="tx-group" data-day-key={key}>
                <div className="tx-day-header">
                  {useLilDays ? (
                    <>
                      <span className="tx-day-num">{lilInfo.day ?? lilInfo.label}</span>
                      <span className="tx-day-wkd">{weekdayShort(date)}</span>
                      <span className="tx-day-monthyear">
                        {lilInfo.special
                          ? `${formatLilYear(lilInfo.year, yearEra)} – ${formatLilYear(lilInfo.year + 1, yearEra)}`
                          : `${lilInfo.monthRoman} · ${formatLilYear(lilInfo.year, yearEra)}`}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="tx-day-num">{date.getDate()}</span>
                      <span className="tx-day-wkd">{weekdayShort(date)}</span>
                      <span className="tx-day-monthyear">
                        {String(date.getMonth() + 1).padStart(2, '0')}/{date.getFullYear()}
                      </span>
                    </>
                  )}
                  <span className="tx-day-totals">
                    {dayIncome > 0 && (
                      <span className="tx-day-amt amount-income">
                        +{formatAmount(dayIncome, dayCur)}
                      </span>
                    )}
                    {dayExpense > 0 && (
                      <span className="tx-day-amt amount-expense">
                        {formatAmount(dayExpense, dayCur)}
                      </span>
                    )}
                  </span>
                </div>
                <div className="list">
                  {items.map(tx => {
                    const category = data.categories.find(c => c.id === tx.categoryId)
                    const cls  = tx.type === 'income' ? 'amount-income' : 'amount-expense'
                    // No sign for expenses (color is already enough). Keep + for income.
                    const sign = tx.type === 'income' ? '+' : ''
                    // Tx synced from gunsi-ledger have a synthetic id prefix
                    // and are read-only here — they're mirrored from Ledger.
                    // For slices, check the parentId (the slice id has #N suffix).
                    const realId = tx._parentId ?? tx.id
                    const fromLedger = typeof realId === 'string' && realId.startsWith('ledger-')
                    // Quota sincronizzata da Even. A differenza di Ledger la
                    // riga resta modificabile: qui gli edit manuali vincono
                    // sulla sync. Il marchio sostituisce l'emoji solo finché
                    // la categoria è quella di sistema: se l'utente l'ha
                    // ricategorizzata a mano, vince la sua icona.
                    const fromEven = typeof realId === 'string' && realId.startsWith('even-')
                      && (!category || isSystemCategory(category))
                    const sliceBadge = tx._sliceIdx != null
                      ? t.transactions.splitBadge
                          .replace('{idx}', tx._sliceIdx + 1)
                          .replace('{total}', tx._sliceTotal)
                      : null
                    return (
                      <div
                        key={tx.id}
                        className={`list-row${fromLedger ? ' from-ledger' : ''}${sliceBadge ? ' is-slice' : ''}`}
                        onClick={fromLedger ? undefined : () => openEdit(tx)}
                        role={fromLedger ? undefined : 'button'}
                        tabIndex={fromLedger ? undefined : 0}
                        aria-disabled={fromLedger || undefined}
                      >
                        <div className="list-row-left">
                          <span className="cat-icon">
                            {fromLedger && !category ? <LedgerMark />
                              : fromEven ? <EvenMark />
                              : <CategoryIcon category={category} size="1.4rem" fallback={tx.type === 'income' ? '💰' : '💸'} />}
                          </span>
                          <div>
                            <div className="list-row-name">
                              {tx.name || category?.name || t.transactions.types[tx.type]}
                              {sliceBadge && (
                                <span className="tx-slice-badge">{sliceBadge}</span>
                              )}
                            </div>
                            <div className="list-row-meta">
                              {(fromLedger
                                ? [category?.name || 'Ledger', tx.note]
                                : [formatTime(displayDate(tx)),
                                   tx.name && category ? category.name : null,
                                   tx.note]
                              ).filter(Boolean).join(' · ')}
                            </div>
                          </div>
                        </div>
                        <div className="list-row-right">
                          <div className="amount-stack">
                            <span className={cls}>
                              {sign}{formatAmount(
                                (tx.currency && tx.currency !== data.currency && tx.amount_base != null)
                                  ? tx.amount_base
                                  : tx.amount,
                                data.currency,
                              )}
                            </span>
                            {tx.currency && tx.currency !== data.currency && (
                              <span className="amount-original">
                                {sign}{formatAmount(tx.amount, tx.currency)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
      </div>
      </div>

      {editing && (
        <TransactionModal
          // Remount when transitioning edit↔create (e.g. on Copy) so state
          // re-initialises from the new `existing` payload.
          key={editing.id ?? (editing._copyTag ?? 'new')}
          t={t}
          existing={editing}
          categories={data.categories}
          defaultCurrency={data.currency}
          isLilAvailable={isLil}
          yearEra={yearEra}
          locale={locale}
          dateFormat={data.dateFormat}
          onClose={() => setEdit(null)}
          onSubmit={(tx) => {
            if (editing.id) updateTransaction(editing.id, tx)
            else addTransaction(tx)
            setEdit(null)
          }}
          onDelete={() => {
            if (editing.id) {
              removeTransaction(editing.id)
              setEdit(null)
            }
          }}
          onCopy={(form) => {
            // Drop the id → modal becomes a create form pre-filled with the
            // selected fields only. Missing fields fall back to modal defaults
            // (empty amount, "now" date/time, default currency, etc).
            const next = { _copyTag: Date.now() } // forces remount on each copy
            if (form.type)               next.type     = form.type
            if (form.amount !== undefined) {
              const v = parseFloat(form.amount)
              if (Number.isFinite(v))    next.amount   = v
            }
            if (form.currency)           next.currency = form.currency
            if (form.date || form.time) {
              const d = form.date || nowDate()
              const t = form.time || nowTime()
              next.date = `${d}T${t}` // naive wall-clock, no TZ conversion
            }
            if (form.categoryId)         next.categoryId = form.categoryId
            if (form.name)               next.name       = form.name
            if (form.note)               next.note       = form.note
            setEdit(next)
          }}
        />
      )}
    </>
  )
}

function nowDate() {
  const d = new Date() // device-local calendar day (not UTC)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function nowTime() {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function formatDateDisplay(iso, locale, dateFormat) {
  if (!iso) return '—'
  return formatDay(iso, dateFormat, locale)
}

function splitIsoDate(iso) {
  // Split a stored date into the picker's YYYY-MM-DD + HH:MM using wall-clock
  // components (timezone-independent). Editing a legacy row therefore shows the
  // original time and re-saves it as a naive wall-clock string.
  const w = wallClock(iso)
  const pad = (n) => String(n).padStart(2, '0')
  if (!w) return { date: '', time: '' }
  return {
    date: `${w.y}-${pad(w.m)}-${pad(w.d)}`,
    time: `${pad(w.hh)}:${pad(w.mm)}`,
  }
}

export function TransactionModal({
  t, existing, categories, defaultCurrency,
  isLilAvailable, yearEra, locale, dateFormat,
  onClose, onSubmit, onDelete, onCopy,
}) {
  // existing can be: null/undefined, {} (blank create), tx-shape with id
  // (edit), tx-shape without id (create-from-copy). Init fields from any
  // shape that carries them.
  const initialDateTime = existing?.date ? splitIsoDate(existing.date) : { date: nowDate(), time: nowTime() }
  const initialEffectiveDT = existing?.effectiveDate
    ? splitIsoDate(existing.effectiveDate)
    : { date: '', time: '' }
  const isEdit = Boolean(existing?.id)
  const [type, setType]         = useState(existing?.type ?? 'expense')
  const [amount, setAmount]     = useState(existing?.amount != null ? String(existing.amount) : '')
  // For NEW tx (no id — i.e. blank create or create-from-copy WITHOUT an
  // explicit currency), default to the most-recently-used currency before
  // the user's base currency. For edits, the existing currency wins.
  const [currency, setCurrency] = useState(
    existing?.currency ?? (isEdit ? null : lastUsedCurrency()) ?? defaultCurrency ?? 'EUR'
  )
  const [date, setDate]         = useState(initialDateTime.date)
  const [time, setTime]         = useState(initialDateTime.time)
  // Effective date+time: when the tx should be shown/grouped (e.g. flight
  // booked in May but the flight is in July → show in July). Empty = use
  // `date`. Time mirrors the payment-row layout for visual symmetry; it
  // ends up in the stored ISO but the bucketing/sorting is day-resolution.
  const [effectiveDate, setEffectiveDate] = useState(initialEffectiveDT.date)
  const [effectiveTime, setEffectiveTime] = useState(initialEffectiveDT.time)
  const [showEffectiveDate, setShowEffectiveDate] = useState(!!initialEffectiveDT.date)
  // Split: distribute the tx across N virtual slices every M units of `unit`,
  // starting at effectiveDate (= first slice). Only meaningful when
  // effectiveDate is active. Stored as strings so empty inputs round-trip
  // cleanly. Accepts legacy splitIntervalDays.
  // splitMode = 'divide' (parent.amount is total, slices = total/N) or
  // 'repeat' (parent.amount is per-occurrence, total = amount × N).
  const initSplitCount = existing?.splitCount ?? null
  const initSplitInt = existing?.splitInterval ?? existing?.splitIntervalDays ?? null
  const initSplitUnit = existing?.splitUnit
    ?? (existing?.splitIntervalDays ? 'days' : null)
  // splitMode is also the on/off switch now — '' means neither active.
  // The segmented control [Suddividi | Ripeti] toggles between '', 'divide',
  // 'repeat' (click same → off, click other → switch).
  const initSplitActive = !!(initSplitCount && initSplitCount >= 2 && initSplitInt && initSplitInt >= 1 && initSplitUnit)
  const initSplitMode = !initSplitActive ? '' : (existing?.splitMode === 'repeat' ? 'repeat' : 'divide')
  const [splitCount, setSplitCount] = useState(initSplitCount ? String(initSplitCount) : '4')
  const [splitInterval, setSplitInterval] = useState(initSplitInt ? String(initSplitInt) : '1')
  const [splitUnit, setSplitUnit] = useState(initSplitUnit || 'weeks')
  const [splitMode, setSplitMode] = useState(initSplitMode)
  const showSplit = splitMode === 'divide' || splitMode === 'repeat'
  const [categoryId, setCategoryId] = useState(existing?.categoryId ?? '')
  const [name, setName]         = useState(existing?.name ?? '')
  const [note, setNote]         = useState(existing?.note ?? '')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [copyOpen, setCopyOpen]     = useState(false)
  const isDesktop = useIsDesktop()
  const dateAnchorRef = useRef(null) // anchors the date popover (button mobile / input desktop)
  const timeAnchorRef = useRef(null) // anchors the time popover (button mobile / input desktop)
  const effDateAnchorRef = useRef(null)
  const effTimeAnchorRef = useRef(null)
  const dateBlurTimerRef = useRef(null)
  const timeBlurTimerRef = useRef(null)
  const effDateBlurTimerRef = useRef(null)
  const effTimeBlurTimerRef = useRef(null)
  function openDatePopover() {
    if (dateBlurTimerRef.current) { clearTimeout(dateBlurTimerRef.current); dateBlurTimerRef.current = null }
    setDateOpen(true)
  }
  function scheduleCloseDatePopover() {
    dateBlurTimerRef.current = setTimeout(() => setDateOpen(false), 200)
  }
  // Close + blur the anchor input — the popover's mousedown.preventDefault
  // keeps focus pinned to the input, so any explicit-close path (X, pick,
  // outside-click, Esc) needs to release focus manually.
  function closeDatePopover() {
    if (dateBlurTimerRef.current) { clearTimeout(dateBlurTimerRef.current); dateBlurTimerRef.current = null }
    setDateOpen(false)
    dateAnchorRef.current?.blur?.()
  }
  function openTimePopover() {
    if (timeBlurTimerRef.current) { clearTimeout(timeBlurTimerRef.current); timeBlurTimerRef.current = null }
    setTimeOpen(true)
  }
  function scheduleCloseTimePopover() {
    timeBlurTimerRef.current = setTimeout(() => setTimeOpen(false), 200)
  }
  function closeTimePopover() {
    if (timeBlurTimerRef.current) { clearTimeout(timeBlurTimerRef.current); timeBlurTimerRef.current = null }
    setTimeOpen(false)
    timeAnchorRef.current?.blur?.()
  }
  function openEffDatePopover() {
    if (effDateBlurTimerRef.current) { clearTimeout(effDateBlurTimerRef.current); effDateBlurTimerRef.current = null }
    setEffDateOpen(true)
  }
  function scheduleCloseEffDatePopover() {
    effDateBlurTimerRef.current = setTimeout(() => setEffDateOpen(false), 200)
  }
  function closeEffDatePopover() {
    if (effDateBlurTimerRef.current) { clearTimeout(effDateBlurTimerRef.current); effDateBlurTimerRef.current = null }
    setEffDateOpen(false)
    effDateAnchorRef.current?.blur?.()
  }
  function openEffTimePopover() {
    if (effTimeBlurTimerRef.current) { clearTimeout(effTimeBlurTimerRef.current); effTimeBlurTimerRef.current = null }
    setEffTimeOpen(true)
  }
  function scheduleCloseEffTimePopover() {
    effTimeBlurTimerRef.current = setTimeout(() => setEffTimeOpen(false), 200)
  }
  function closeEffTimePopover() {
    if (effTimeBlurTimerRef.current) { clearTimeout(effTimeBlurTimerRef.current); effTimeBlurTimerRef.current = null }
    setEffTimeOpen(false)
    effTimeAnchorRef.current?.blur?.()
  }
  function revealEffectiveDate() {
    setShowEffectiveDate(true)
    // Seed with the payment date+time so the row isn't blank on open.
    if (!effectiveDate) setEffectiveDate(date)
    if (!effectiveTime) setEffectiveTime(time)
    setTimeout(() => {
      effDateAnchorRef.current?.focus?.()
      setEffDateOpen(true)
    }, 0)
  }
  function clearEffectiveDate() {
    setShowEffectiveDate(false)
    setEffectiveDate('')
    setEffectiveTime('')
    setEffDateOpen(false)
    setEffTimeOpen(false)
    // Split requires effectiveDate as first slice — collapse it too.
    setSplitMode('')
  }
  // Click the same mode button → deselect (close everything). Click the
  // other → switch and stay open.
  function pickMode(m) {
    setSplitMode(prev => prev === m ? '' : m)
  }
  // Default selection: everything except date+time (user almost always wants
  // the copy to land on "now"). `type` is always copied (no checkbox);
  // `amount` controls both amount + currency (copying one alone is useless).
  const [copyFields, setCopyFields] = useState(
    () => new Set(['amount', 'categoryId', 'name', 'note'])
  )
  const [dateOpen, setDateOpen] = useState(false)
  const [timeOpen, setTimeOpen] = useState(false)
  const [effDateOpen, setEffDateOpen] = useState(false)
  const [effTimeOpen, setEffTimeOpen] = useState(false)

  // Track the visualViewport so the modal stops exactly where the mobile
  // soft keyboard begins (Android Chrome shrinks visualViewport.height when
  // the keyboard opens; iOS Safari does the same).
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const update = () => {
      document.documentElement.style.setProperty('--vv-h', `${vv.height}px`)
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      document.documentElement.style.removeProperty('--vv-h')
    }
  }, [])

  function changeType(newType) {
    setType(newType)
    if (categoryId) {
      const cat = categories.find(c => c.id === categoryId)
      if (cat && cat.type !== newType) setCategoryId('')
    }
  }

  // Hoisted split validity — used by the inputs (red border) and the save
  // button (disabled when split is open but values are invalid).
  const splitCountNum    = parseInt(splitCount, 10)
  const splitIntervalNum = parseInt(splitInterval, 10)
  const splitCountValid    = Number.isInteger(splitCountNum) && splitCountNum >= 2
  const splitIntervalValid = Number.isInteger(splitIntervalNum) && splitIntervalNum >= 1
  const splitFormInvalid   = showSplit && (!splitCountValid || !splitIntervalValid)

  function submit(e) {
    e.preventDefault()
    const value = parseFloat(amount)
    if (!Number.isFinite(value) || value <= 0) return
    if (splitFormInvalid) return
    // effectiveDate: combine the picked date+time (local TZ) into an ISO.
    // Time defaults to "00:00" if the user left it blank. Day bucketing is
    // independent of time, but storing the time keeps symmetry with the
    // payment row and round-trips through edit cleanly.
    let effIso = null
    if (showEffectiveDate && effectiveDate) {
      const t = effectiveTime || '00:00'
      effIso = `${effectiveDate}T${t}` // naive wall-clock, no TZ conversion
    }
    // Split: only emit when the accordion is open AND values are valid (≥2
    // slices, ≥1 interval, valid unit). Backend also drops split when
    // effectiveDate is absent, but we mirror the logic client-side so the
    // payload is clean.
    let sc = null, si = null, su = null, sm = null
    if (showSplit && effIso) {
      const c = parseInt(splitCount, 10)
      const i = parseInt(splitInterval, 10)
      if (Number.isInteger(c) && c >= 2 && Number.isInteger(i) && i >= 1 && splitUnit) {
        sc = c; si = i; su = splitUnit
        sm = splitMode === 'repeat' ? 'repeat' : 'divide'
      }
    }
    onSubmit({
      type,
      amount: value,
      currency,
      date: `${date}T${time}`, // naive wall-clock, no TZ conversion
      effectiveDate: effIso, // null clears any existing effectiveDate
      splitCount: sc,        // null clears any existing split
      splitInterval: si,
      splitUnit: su,
      splitMode: sm,
      categoryId: categoryId || undefined,
      name: name.trim() || undefined,
      note: note.trim() || undefined,
    })
  }

  return (
    <div
      className="modal-backdrop modal-backdrop-fullscreen"
      // On desktop, an accidental backdrop click while editing is very
      // annoying — disable it. Mobile is fullscreen anyway, so no backdrop
      // is even visible to click.
      onClick={isDesktop ? undefined : onClose}
    >
      <form className="modal tx-modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
        {!isDesktop && (
          <button
            type="button"
            className="modal-close-btn tx-modal-close"
            onClick={onClose}
            aria-label={t.common.close}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        <div className="type-toggle" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={type === 'expense'}
            className={`type-toggle-btn${type === 'expense' ? ' active expense' : ''}`}
            onClick={() => changeType('expense')}
          >
            {t.transactions.types.expense}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={type === 'income'}
            className={`type-toggle-btn${type === 'income' ? ' active income' : ''}`}
            onClick={() => changeType('income')}
          >
            {t.transactions.types.income}
          </button>
        </div>

        <div className="tx-amount-row">
          <AmountInput
            value={amount}
            onChange={setAmount}
            placeholder="0.00"
            autoFocus={!existing}
          />
          <CurrencySelect
            value={currency}
            onChange={setCurrency}
            searchPlaceholder={t.common.searchCurrency}
            title={t.transactions.currency}
            closeLabel={t.common.close}
            className="tx-currency-wrap"
          />
        </div>

        <div className="tx-row tx-row-date">
          <button
            type="button"
            className={`tx-row-label tx-row-label-toggle${showEffectiveDate ? ' open' : ''}`}
            onClick={showEffectiveDate ? clearEffectiveDate : revealEffectiveDate}
            aria-expanded={showEffectiveDate}
            aria-label={
              showEffectiveDate
                ? t.transactions.removeEffectiveDate
                : t.transactions.showOnDifferentDate
            }
            title={
              showEffectiveDate
                ? t.transactions.removeEffectiveDate
                : t.transactions.showOnDifferentDate
            }
          >
            <span>
              {showEffectiveDate ? t.transactions.paymentDate : t.transactions.date}
            </span>
            <svg
              className="tx-effdate-chev"
              width="12" height="12" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            >
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
          {isDesktop ? (
            <input
              ref={dateAnchorRef}
              type="date"
              className="tx-date-input"
              value={date}
              onChange={e => setDate(e.target.value)}
              // Suppress native browser date picker on click by killing the
              // default action. Then manually focus the input so our custom
              // popover opens via onFocus. Keyboard tab-in still works (no
              // click event → no preventDefault → native picker doesn't open
              // on keyboard focus either in Chrome/Firefox).
              onMouseDown={e => {
                e.preventDefault()
                dateAnchorRef.current?.focus()
              }}
              onFocus={openDatePopover}
              onBlur={scheduleCloseDatePopover}
              aria-label={showEffectiveDate ? t.transactions.paymentDate : t.transactions.date}
            />
          ) : (
            <button
              ref={dateAnchorRef}
              type="button"
              className="tx-date-btn"
              onClick={() => setDateOpen(true)}
              aria-label={showEffectiveDate ? t.transactions.paymentDate : t.transactions.date}
            >
              {formatDateDisplay(date, locale, dateFormat)}
            </button>
          )}
          {isDesktop ? (
            <input
              ref={timeAnchorRef}
              type="time"
              className="tx-time-input"
              value={time}
              onChange={e => setTime(e.target.value)}
              onMouseDown={e => {
                e.preventDefault()
                timeAnchorRef.current?.focus()
              }}
              onFocus={openTimePopover}
              onBlur={scheduleCloseTimePopover}
              aria-label={t.transactions.time}
            />
          ) : (
            <button
              ref={timeAnchorRef}
              type="button"
              className="tx-time-btn"
              onClick={() => setTimeOpen(true)}
              aria-label={t.transactions.time}
            >
              {time}
            </button>
          )}
        </div>

        {showEffectiveDate && (
          <div className="tx-row tx-row-date tx-row-effdate">
            <span className="tx-row-label">{t.transactions.effectiveDate}</span>
            {isDesktop ? (
              <input
                ref={effDateAnchorRef}
                type="date"
                className="tx-date-input"
                value={effectiveDate}
                onChange={e => setEffectiveDate(e.target.value)}
                onMouseDown={e => {
                  e.preventDefault()
                  effDateAnchorRef.current?.focus()
                }}
                onFocus={openEffDatePopover}
                onBlur={scheduleCloseEffDatePopover}
                aria-label={t.transactions.effectiveDate}
              />
            ) : (
              <button
                ref={effDateAnchorRef}
                type="button"
                className="tx-date-btn"
                onClick={() => setEffDateOpen(true)}
                aria-label={t.transactions.effectiveDate}
              >
                {effectiveDate ? formatDateDisplay(effectiveDate, locale, dateFormat) : "—"}
              </button>
            )}
            {isDesktop ? (
              <input
                ref={effTimeAnchorRef}
                type="time"
                className="tx-time-input"
                value={effectiveTime}
                onChange={e => setEffectiveTime(e.target.value)}
                onMouseDown={e => {
                  e.preventDefault()
                  effTimeAnchorRef.current?.focus()
                }}
                onFocus={openEffTimePopover}
                onBlur={scheduleCloseEffTimePopover}
                aria-label={t.transactions.time}
              />
            ) : (
              <button
                ref={effTimeAnchorRef}
                type="button"
                className="tx-time-btn"
                onClick={() => setEffTimeOpen(true)}
                aria-label={t.transactions.time}
              >
                {effectiveTime || '—'}
              </button>
            )}
          </div>
        )}

        <DatePicker
          open={dateOpen}
          value={date}
          onChange={setDate}
          onClose={closeDatePopover}
          isLilAvailable={isLilAvailable}
          yearEra={yearEra}
          locale={locale}
          t={t}
          anchorRef={dateAnchorRef}
        />
        <TimePicker
          open={timeOpen}
          value={time}
          onChange={setTime}
          onClose={closeTimePopover}
          t={t}
          anchorRef={timeAnchorRef}
        />
        <DatePicker
          open={effDateOpen}
          value={effectiveDate}
          onChange={setEffectiveDate}
          onClose={closeEffDatePopover}
          isLilAvailable={isLilAvailable}
          yearEra={yearEra}
          locale={locale}
          t={t}
          anchorRef={effDateAnchorRef}
        />
        <TimePicker
          open={effTimeOpen}
          value={effectiveTime}
          onChange={setEffectiveTime}
          onClose={closeEffTimePopover}
          t={t}
          anchorRef={effTimeAnchorRef}
        />

        {showEffectiveDate && (
          <div className="tx-row tx-row-split">
            {/* Empty label keeps the same indent as the rows above so the
                stack (mode + controls + preview) aligns under the input
                column, not the label column. */}
            <span className="tx-row-label" aria-hidden="true" />
            <div className="tx-split-stack">
              <div className="tx-split-mode" role="tablist" aria-label={t.transactions.splitToggleQuestion}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={splitMode === 'divide'}
                  className={`tx-split-mode-btn${splitMode === 'divide' ? ' active' : ''}`}
                  onClick={() => pickMode('divide')}
                >
                  {t.transactions.split}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={splitMode === 'repeat'}
                  className={`tx-split-mode-btn${splitMode === 'repeat' ? ' active' : ''}`}
                  onClick={() => pickMode('repeat')}
                >
                  {t.transactions.repeat}
                </button>
              </div>
              {showSplit && (() => {
              // When the user is in Lil mode, expose both Greg and Lil
              // flavours of months/years. Otherwise show a simplified list
              // (days, weeks, months, years) — "months" maps to months_greg
              // (and same for years) since a non-Lil user doesn't care.
              // Each option carries the i18n {one, other} object; we pick
              // the singular/plural form based on the current interval.
              const intervalNum = parseInt(splitInterval, 10)
              const isOne = intervalNum === 1
              const pick = (key, labelKey) => {
                const o = t.transactions.splitUnit[labelKey]
                return { v: key, l: (isOne ? o.one : o.other) }
              }
              const unitOptions = isLilAvailable
                ? [
                    pick('days',        'days'),
                    pick('weeks',       'weeks'),
                    pick('months_greg', 'months_greg'),
                    pick('months_lil',  'months_lil'),
                    pick('years_greg',  'years_greg'),
                    pick('years_lil',   'years_lil'),
                  ]
                : [
                    pick('days',        'days'),
                    pick('weeks',       'weeks'),
                    pick('months_greg', 'months'),
                    pick('years_greg',  'years'),
                  ]
              return (
                <div className="tx-split-controls">
                  <div className="tx-split-group">
                    <IntegerInput
                      value={splitCount}
                      onChange={setSplitCount}
                      min={2}
                      className="tx-split-num"
                      invalid={!splitCountValid}
                      ariaLabel={t.transactions.splitCount}
                    />
                    <span className="tx-split-word">{t.transactions.splitCount}</span>
                    <span className="tx-split-word">{t.transactions.splitInterval}</span>
                  </div>
                  <div className="tx-split-group">
                    <IntegerInput
                      value={splitInterval}
                      onChange={setSplitInterval}
                      min={1}
                      className="tx-split-num"
                      invalid={!splitIntervalValid}
                      ariaLabel={t.transactions.splitInterval}
                    />
                    <SimpleSelect
                      value={splitUnit}
                      onChange={setSplitUnit}
                      options={unitOptions.map(o => ({ value: o.v, label: o.l }))}
                      triggerClassName="tx-split-unit"
                      ariaLabel="unit"
                    />
                  </div>
                </div>
              )
            })()}
              {showSplit && (() => {
                const c = parseInt(splitCount, 10)
                const i = parseInt(splitInterval, 10)
                const v = parseFloat(amount)
                if (!Number.isInteger(c) || c < 2) return null
                if (!Number.isInteger(i) || i < 1) return null
                if (!Number.isFinite(v) || v <= 0) return null
                if (!effectiveDate || !splitUnit) return null
                // divide: per = v/c, total = v
                // repeat: per = v,   total = v*c
                const per   = splitMode === 'repeat' ? v       : v / c
                const total = splitMode === 'repeat' ? v * c   : v
                const startIso = `${effectiveDate}T00:00` // naive wall-clock
                const endIso = addInterval(startIso, (c - 1) * i, splitUnit)
                const fmt = (iso) => formatDateDisplay(iso.slice(0, 10), locale, dateFormat)
                const perStr   = formatAmount(per, currency)
                const totalStr = formatAmount(total, currency)
                const amountTxt = t.transactions.splitPreviewAmount.replace('{amount}', perStr)
                const totalTxt  = t.transactions.splitPreviewTotal.replace('{total}', totalStr)
                const rangeTxt  = t.transactions.splitPreviewRange
                  .replace('{from}', fmt(startIso))
                  .replace('{to}', fmt(endIso))
                return (
                  <div className="tx-split-preview">
                    <span className="tx-split-preview-amount">{amountTxt}</span>
                    <span className="tx-split-preview-sep" aria-hidden="true">·</span>
                    <span className="tx-split-preview-total">{totalTxt}</span>
                    <span className="tx-split-preview-sep" aria-hidden="true">·</span>
                    <span className="tx-split-preview-range">{rangeTxt}</span>
                  </div>
                )
              })()}
            </div>
          </div>
        )}

        <label className="tx-row">
          <span className="tx-row-label">{t.transactions.category}</span>
          <CategoryPicker
            value={categoryId}
            onChange={setCategoryId}
            categories={categories.filter(c => c.type === type)}
            chooseLabel={t.common.choose}
            cancelLabel={t.common.cancel}
            noneLabel={t.common.none}
            searchPlaceholder={t.common.searchCategory}
            title={t.transactions.category}
            closeLabel={t.common.close}
          />
        </label>

        <label className="tx-row tx-row-name">
          <span className="tx-row-label">{t.transactions.name}</span>
          <NameInput
            value={name}
            onChange={setName}
            placeholder={t.transactions.namePlaceholder}
          />
        </label>

        <label className="tx-row tx-row-note">
          <span className="tx-row-label">{t.transactions.notes}</span>
          <textarea
            placeholder={t.transactions.notesPlaceholder}
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
          />
        </label>

        <div className="modal-actions">
          {isEdit && (
            <button
              type="button"
              className="btn btn-danger-outline"
              onClick={() => setDeleteOpen(true)}
            >
              {t.common.delete}
            </button>
          )}
          {isEdit && (
            <button
              type="button"
              className="btn"
              onClick={() => setCopyOpen(true)}
            >
              {t.transactions.copy}
            </button>
          )}
          <span style={{ flex: 1 }} />
          {isDesktop && (
            <button type="button" className="btn" onClick={onClose}>
              {t.common.cancel}
            </button>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!amount || parseFloat(amount) <= 0 || splitFormInvalid}
          >
            {t.common.save}
          </button>
        </div>
      </form>

      {deleteOpen && (
        <div
          className="modal-backdrop modal-backdrop-nested"
          onClick={() => setDeleteOpen(false)}
        >
          <div className="modal tx-delete-confirm" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">
              {showSplit && Number(splitCount) >= 2
                ? t.transactions.deleteAllSlicesConfirm.replace('{n}', splitCount)
                : t.transactions.deleteConfirm}
            </h3>
            <p className="modal-sub">{t.transactions.deleteConfirmSub}</p>
            <div className="tx-delete-summary">
              <span className={`tx-delete-amount ${type === 'income' ? 'amount-income' : 'amount-expense'}`}>
                {(type === 'income' ? '+' : '−')}
                {formatAmount(parseFloat(amount) || 0, currency)}
              </span>
              {(name || categoryId) && (
                <span className="tx-delete-meta">
                  {name || categories.find(c => c.id === categoryId)?.name || t.transactions.types[type]}
                </span>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => setDeleteOpen(false)}>
                {t.common.cancel}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => { setDeleteOpen(false); onDelete() }}
              >
                {t.common.delete}
              </button>
            </div>
          </div>
        </div>
      )}

      {copyOpen && (
        <div
          className="modal-backdrop modal-backdrop-nested"
          onClick={() => setCopyOpen(false)}
        >
          <div className="modal tx-copy-confirm" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">{t.transactions.copyConfirm}</h3>
            <p className="modal-sub">{t.transactions.copyConfirmSub}</p>
            <div className="tx-copy-fields">
              {[
                { key: 'date',       label: t.transactions.date },
                { key: 'time',       label: t.transactions.time },
                { key: 'amount',     label: t.transactions.amount },
                { key: 'categoryId', label: t.transactions.category },
                { key: 'name',       label: t.transactions.name },
                { key: 'note',       label: t.transactions.notes },
              ].map(f => (
                <label key={f.key} className="tx-copy-field">
                  <input
                    type="checkbox"
                    checked={copyFields.has(f.key)}
                    onChange={e => {
                      setCopyFields(prev => {
                        const next = new Set(prev)
                        if (e.target.checked) next.add(f.key); else next.delete(f.key)
                        return next
                      })
                    }}
                  />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => setCopyOpen(false)}>
                {t.common.cancel}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  // Type is always copied. Amount checkbox covers currency too.
                  const snapshot = { type }
                  if (copyFields.has('amount')) {
                    snapshot.amount   = amount
                    snapshot.currency = currency
                  }
                  if (copyFields.has('date'))       snapshot.date       = date
                  if (copyFields.has('time'))       snapshot.time       = time
                  if (copyFields.has('categoryId')) snapshot.categoryId = categoryId
                  if (copyFields.has('name'))       snapshot.name       = name
                  if (copyFields.has('note'))       snapshot.note       = note
                  setCopyOpen(false)
                  onCopy?.(snapshot)
                }}
              >
                {t.transactions.copy}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
